// src/pages/LoginPage.jsx
import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import "../components/landing.css";

const LEGAL_LINKS = [
  { to: "/terms-and-conditions", label: "Terms and Conditions" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/refund-policy", label: "Refund Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
  { to: "/legal-notice", label: "Legal Notice" },
];

function isSubscriptionActive(subscriptionStatus) {
  return subscriptionStatus === "active" || subscriptionStatus === "trialing";
}
function getAuthErrorMessage(error, context = "login") {
  const code = error?.code;

  if (context === "reset") {
    if (code === "auth/invalid-email") {
      return "Please enter a valid email address.";
    }

    if (code === "auth/too-many-requests") {
      return "Too many requests. Please wait and try again later.";
    }

    if (code === "auth/network-request-failed") {
      return "Network error. Please check your connection and try again.";
    }

    return "Unable to send the password reset email. Please try again.";
  }

  if (
    code === "auth/invalid-credential" ||
    code === "auth/user-not-found" ||
    code === "auth/wrong-password"
  ) {
    return "Email or password is incorrect.";
  }

  if (code === "auth/invalid-email") {
    return "Please enter a valid email address.";
  }

  if (code === "auth/missing-password") {
    return "Please enter your password.";
  }

  if (code === "auth/too-many-requests") {
    return "Too many failed login attempts. Please wait and try again, or reset your password.";
  }

  if (code === "auth/network-request-failed") {
    return "Network error. Please check your connection and try again.";
  }

  if (code === "auth/user-disabled") {
    return "This account has been disabled. Please contact support.";
  }

  return "Login failed. Please check your details and try again.";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Where the user wanted to go before being redirected to login
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setErr("");
    setInfo("");
    setBusy(true);

    try {
      // Keep the user logged in only for the current browser session.
      // Closing the browser/tab will end the saved Firebase session.
      await setPersistence(auth, browserSessionPersistence);

      // 1) Auth login
      const cred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      const uid = cred.user.uid;

      // 2) Load user doc: role + onboarding + subscription
      let role = "user";
      let onboardingCompleted = false;
      let subscriptionStatus = "inactive";

      try {
        const ref = doc(db, "users", uid);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data();
          role = data.role || "user";
          onboardingCompleted = !!data.onboardingCompleted;
          subscriptionStatus = data.subscriptionStatus || "inactive";
        }
      } catch (e) {
        console.error("Error reading user doc on login", e);
        // Fallback safe: treat as non-subscribed
      }

      // 3) Routing logic
      if (role === "admin") {
        navigate("/admin", { replace: true });
        return;
      }

      const active = isSubscriptionActive(subscriptionStatus);

      // Not active → pricing page
      if (!active) {
        navigate("/pricing", {
          replace: true,
          state: { from: { pathname: from } },
        });
        return;
      }

      // Active but onboarding not completed → onboarding
      if (!onboardingCompleted) {
        navigate("/onboarding", { replace: true });
        return;
      }

      // Active + onboarded:
      // never send an already subscribed user back to payment-related pages
      const paymentRoutes = ["/pricing", "/checkout", "/payment-success"];

      if (paymentRoutes.includes(from)) {
        navigate("/dashboard", { replace: true });
        return;
      }

      // For protected app pages, preserve the intended destination
      navigate(from || "/dashboard", { replace: true });
   } catch (e) {
  console.error("Login error:", e);
  setErr(getAuthErrorMessage(e, "login"));
} finally {
  setBusy(false);
}
  }

  async function handleForgotPassword() {
    setErr("");
    setInfo("");

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setErr('Please enter your email and then click "Forgot my password".');
      return;
    }

    setBusy(true);

    try {
      await sendPasswordResetEmail(auth, trimmedEmail);
      setInfo("Password reset email sent. Check your inbox and spam folder.");
} catch (e) {
  console.error("Password reset error:", e);
  setErr(getAuthErrorMessage(e, "reset"));
} finally {
  setBusy(false);
}
  }

  return (
    <div className="landing" style={{ alignItems: "center" }}>
      <main className="landing__main" style={{ maxWidth: 420 }}>
        <h1 className="landing__title">Welcome back</h1>

  <p className="landing__subtitle">
  Log in to access your assessments.
</p>

{location.state?.sessionExpired && (
  <div
    style={{
      marginTop: 16,
      padding: "12px 14px",
      borderRadius: 12,
      background: "#F0FDF4",
      border: "1px solid #BBF7D0",
      color: "#166534",
      fontSize: 14,
      lineHeight: 1.5,
    }}
  >
    You were logged out automatically after 30 minutes of inactivity. Please
    log in again to continue.
  </div>
)}

<form onSubmit={handleLogin} style={{ marginTop: 16 }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl"
            style={inputStyle}
            required
            autoComplete="email"
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl"
            style={inputStyle}
            required
            autoComplete="current-password"
          />

          <button
            className="btn btn--primary"
            style={{ width: "100%", marginTop: 8 }}
            disabled={busy}
            type="submit"
          >
            {busy ? "Signing in…" : "Log in"}
          </button>
        </form>

        <div style={{ marginTop: 10, textAlign: "center" }}>
          <button
            type="button"
            className="btn btn--ghost"
            style={{ width: "100%" }}
            onClick={handleForgotPassword}
            disabled={busy}
          >
            Forgot my password
          </button>
        </div>

        {err && (
          <p style={{ color: "#b91c1c", marginTop: 12, fontSize: 14 }}>
            {err}
          </p>
        )}

        {info && (
          <p style={{ color: "#059669", marginTop: 8, fontSize: 14 }}>
            {info}
          </p>
        )}

        <p className="landing__subtitle" style={{ marginTop: 14 }}>
          First time here?{" "}
          <Link
            to="/pricing"
            style={{ color: "#059669", textDecoration: "underline" }}
          >
            See plans & subscribe
          </Link>
        </p>

        <LoginLegalFooter />
      </main>
    </div>
  );
}

function LoginLegalFooter() {
  return (
    <footer
      style={{
        marginTop: "2rem",
        paddingTop: "1.25rem",
        borderTop: "1px solid #e5e7eb",
        textAlign: "center",
        fontSize: "0.8rem",
        color: "#64748b",
      }}
    >
      <div
        style={{
          marginBottom: 8,
          fontSize: 12,
          color: "#64748b",
        }}
      >
        Legal documents
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.65rem",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          lineHeight: 1.8,
        }}
      >
        {LEGAL_LINKS.map((item, index) => (
          <React.Fragment key={item.to}>
            <Link
              to={item.to}
              style={{
                color: "#148A58",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              {item.label}
            </Link>

            {index < LEGAL_LINKS.length - 1 && (
              <span style={{ color: "#CBD5E1" }}>•</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </footer>
  );
}

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  outline: "none",
  marginTop: 8,
};


